import { Fragment, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import dayjs from 'dayjs';
import QRcode from 'qrcode';
import { isAfter } from 'date-fns';
import { Button, CentredSpinner, Icon } from '@velove/design-system';
import { Box, Divider, Flex, Link, Text } from 'theme-ui';
import { Alert, Col, Container, Modal, Row } from 'react-bootstrap';
import { AppContext } from '../../contexts/AppContext';
import { DeliveryVerificationMethod } from '../../contexts/contextTypes';
// @ts-ignore
import BankID from '../../assets/images/BankID_logo_white.svg';
// @ts-ignore
import Refresh from '../../assets/images/refresh.svg';

const BankIDVerification = ({ isCustomerService }: { isCustomerService: boolean }) => {
  const { t } = useTranslation();
  const { settings, trackingDetails, setBankIdOderRef, bankId } = useContext(AppContext);
  const { packageId } = useParams();
  const [qr, setQr] = useState<string | null>(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bankIdDetails, setBankIdDetails] = useState<{
    orderRef: string;
    expirationDateTime: Date;
  } | null>(null);
  const [verificationCode, setVerificationCode] = useState<string | null>(null);
  const [isBankIdAppSelected, setIsBankIdAppSelected] = useState<boolean | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isLoading, setIsLoading] = useState(false);

  const verifyByBankIdCode = settings?.deliveryVerificationMethods?.['checkName']
    ? Object.values(settings.deliveryVerificationMethods['checkName']).includes(DeliveryVerificationMethod.BANKID_CODE)
    : false;

  const requiresBankIdCodeRecipientMatch = verifyByBankIdCode && trackingDetails?.task?.recipientMustMatchEndCustomer;

  const handleResize = () => {
    setIsMobile(window.innerWidth < 768);
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    if (bankId.orderRef) {
      const currentDateTime = new Date();
      setIsBankIdAppSelected(true);
      setIsLoading(true);
      setBankIdDetails({
        orderRef: bankId.orderRef,
        expirationDateTime: new Date(currentDateTime.setSeconds(currentDateTime.getSeconds() + 2))
      });
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      setBankIdOderRef(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isBankIdDetailsNotExpired = (details: { orderRef: string; expirationDateTime: Date }) => {
    return isAfter(details.expirationDateTime, new Date());
  };

  const InitiateBankIDMutation = useMutation({
    mutationFn: () => {
      return axios.post(
        `${process.env.REACT_APP_API_ENDPOINT}/v1/bankid/initiate`,
        {
          internalPackageId: trackingDetails?.package?.internalPackageId,
          taskId: trackingDetails?.task?.id
        },
        {
          headers: {
            'content-type': 'application/json'
          }
        }
      );
    },
    onSuccess: async ({ data }) => {
      if (isBankIdAppSelected) {
        const trackingLink = settings?.trackingUrl || 'https://consumer.velove.se';
        window.location.href = `https://app.bankid.com/?autostarttoken=${data.autoStartToken}&redirect=${trackingLink}/p/${packageId}?orderref=${data.orderRef}`;
      } else {
        const currentDateTime = new Date();

        setBankIdOderRef(data.orderRef);
        setBankIdDetails({
          orderRef: data.orderRef,
          expirationDateTime: new Date(currentDateTime.setMinutes(currentDateTime.getMinutes() + 2))
        });

        let Qr = await QRcode.toDataURL(data?.qrContent, { scale: 8 });
        setQr(Qr);
      }
    },
    onError: () => {
      setError('serverErr');
    }
  });

  useQuery({
    queryKey: ['collect', bankIdDetails],
    queryFn: async () => {
      const response = await axios.get(`${process.env.REACT_APP_API_ENDPOINT}/v1/bankid/collect/${bankIdDetails?.orderRef}`, {
        headers: {
          'content-type': 'application/json'
        },
        params: {
          internalPackageId: trackingDetails?.package?.internalPackageId,
          taskId: trackingDetails?.task?.id,
          shiftId: trackingDetails?.route?.shiftId
        }
      });
      return response.data;
    },
    onSuccess: async (data) => {
      if (isBankIdAppSelected) {
        if (data.verificationCode) {
          setVerificationCode(data.verificationCode);
        } else {
          setError(data.errorMessage);
        }

        setIsLoading(false);
        setBankIdDetails(null);
        setBankIdOderRef(null);
      } else {
        if (data.verificationCode) {
          setVerificationCode(data.verificationCode);
          handleCloseQrModal();
        } else if (data.qrContent) {
          let Qr = await QRcode.toDataURL(data?.qrContent, { scale: 8 });
          setQr(Qr);
        } else {
          setQr(null);
          setBankIdDetails(null);
          setError(data.errorMessage);
        }
      }
    },
    onError: () => {
      if (isBankIdAppSelected) {
        setIsLoading(false);
      } else {
        setQr(null);
      }

      setBankIdDetails(null);
      setError('serverErr');
    },
    refetchInterval: 5 * 1000,
    enabled: !!bankIdDetails && isBankIdDetailsNotExpired(bankIdDetails)
  });

  const initiateBankId = async (isAppSelected: boolean) => {
    await setIsBankIdAppSelected(isAppSelected);
    setError(null);

    if (!isAppSelected) setShowQRModal(true);

    InitiateBankIDMutation.mutate();
  };

  const handleCloseQrModal = () => {
    setQr(null);
    setShowQRModal(false);
    setBankIdOderRef(null);
    setError(null);
    setBankIdDetails(null);
  };

  const displayVerificationCode = (code: string) => {
    return code.split('').map((item: string) => (
      <Box className="code-box" mr={2} ml={2}>
        <b>{item}</b>
      </Box>
    ));
  };

  const displayBankIdButton =
    !isCustomerService && trackingDetails?.task?.completeAfter && dayjs(trackingDetails?.task?.completeAfter).diff(dayjs(), 'minute') <= 180;

  let enabledTime = '14:00;';
  if (trackingDetails?.task?.completeAfter) {
    enabledTime = dayjs(trackingDetails?.task?.completeAfter).subtract(3, 'hour').format('HH:mm');
  }

  // Return null if neither BankID verification method should be shown
  if (!requiresBankIdCodeRecipientMatch) {
    return null;
  }

  return (
    <Fragment>
      {verificationCode || trackingDetails?.task?.verificationCode ? (
        <div className="verification-body">
          <p>{t('bankID.verifiedMessage')}</p>
          <Flex className="verification-content">{displayVerificationCode(trackingDetails?.task?.verificationCode || verificationCode || '')}</Flex>
        </div>
      ) : isLoading ? (
        <div style={{ margin: '0 auto' }}>
          <CentredSpinner size="40" />
        </div>
      ) : (
        <Container>
          <Row>
            <Flex className="bankid-container">
              <Button
                className="bankid-content"
                pt={'5px'}
                pb={'5px'}
                onClick={() => initiateBankId(isMobile)}
                disabled={!displayBankIdButton}
                sx={{
                  opacity: displayBankIdButton ? 1 : 0.5
                }}
              >
                <img src={BankID} width={40} height={40} alt="BankID_logo" />
                {isMobile ? t('bankID.buttonForSameDevice') : t('bankID.buttonForAnotherDevice')}
              </Button>
            </Flex>
          </Row>
          {displayBankIdButton && (
            <Row style={{ marginTop: '15px' }}>
              <Link onClick={() => initiateBankId(!isMobile)} className="bankid-optional-link">
                <b>{isMobile ? t('bankID.LinkForAnotherDevice') : t('bankID.LinkForSameDevice')}</b>
              </Link>
            </Row>
          )}
          {isBankIdAppSelected && error && (
            <Row>
              <Flex className="bankid-error-content">
                <Alert key={'danger'} variant={'danger'} style={{ padding: 10 }}>
                  <Row>
                    <Col
                      xxs={2}
                      sm={2}
                      md={2}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Icon icon="Warning" className="bankid-error-icon"></Icon>
                    </Col>
                    <Col xxs={10} sm={10} md={10}>
                      <Text className="bankid-error-text">{t('bankID.errors.' + error)}</Text>
                    </Col>
                  </Row>
                </Alert>
              </Flex>
            </Row>
          )}
          <Row>
            <Flex className="bankid-description-content">
              <p className="bankid-description-text">{t('deliveryRequirements.bankIdRequired').replace(/(##estimatedTime##)/, enabledTime)}</p>
            </Flex>
          </Row>
        </Container>
      )}
      <Modal show={showQRModal} onHide={() => handleCloseQrModal()} animation={false} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('bankID.modal.header')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Box m={3}>
            <p>{t('bankID.modal.subHeader')}</p>
            {qr ? (
              <Fragment>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center'
                  }}
                >
                  <img alt="BankID QR" src={qr} width="250" height="250" />
                </div>
              </Fragment>
            ) : error ? (
              <Alert key={'danger'} variant={'danger'}>
                {t('bankID.errors.' + error)}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'right',
                    marginTop: '10px'
                  }}
                >
                  <Button
                    style={{ backgroundColor: '#CD5C5C' }}
                    onClick={() => {
                      InitiateBankIDMutation.mutate();
                      setError(null);
                    }}
                  >
                    <img src={Refresh} width={20} height={20} alt="refresh_icon" style={{ marginRight: 10 }} />
                    Refresh
                  </Button>
                </div>
              </Alert>
            ) : (
              <div style={{ margin: '0 auto' }}>
                <CentredSpinner size="40" />
              </div>
            )}
          </Box>
        </Modal.Body>
      </Modal>
      <Divider />
    </Fragment>
  );
};

export default BankIDVerification;
